import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/Form";
import { useAssociatedPartnerData } from "@/contexts/PartnerContext";
import { verifyAssociated } from "@/services/verify-associated";
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Input } from "@youvisa/components";
import CryptoJS from "crypto-js";
import { GetServerSideProps } from "next";
import Image from "next/image";
import { useRouter } from "next/router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as z from 'zod';
import DynamicLayout from "./layout";

type CheckPartnershipResponse = {
  is_member: boolean;
  related_companies: {
    cnpj: string,
    name: string,
    city: string,
    state: string,
    business_line: string;
    account_manager: string;
    classification: string;
    client_code: string;
    client_store: string;
    associate_at: string;
  }[];
};

type CheckPartnershipErrorResponse = {
  detail: {
    loc: string[];
    msg: string;
    type: string;
  }[];
};

const applicantSchema = z.object({
  email: z
    .string({ required_error: 'O e-mail é necessário!' })
    .email({ message: 'O e-mail é inválido!' })
});

export const getServerSideProps: GetServerSideProps = async ({ req, query }) => {
  const { token } = query;

  let decryptedData = null;

  if (token && typeof token === 'string') {
    try {
      const decodedToken = decodeURIComponent(token);

      decryptedData = CryptoJS.AES.decrypt(decodedToken, process.env.AMCHAM_CRYPTO_KEY!).toString(CryptoJS.enc.Utf8);

      const associateData: AssociateData = JSON.parse(decryptedData);

      return { redirect: { destination: `/payment?token=${encodeURIComponent(token)}`, permanent: false } };
    } catch (error: any) {
      console.error(error.message)
    }
  }

  return { props: { associate_data: decryptedData } };
};

type HomeProps = {
  associate_data: AssociateData;
}

const Home: NextPageWithLayout<HomeProps> = ({ associate_data }) => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const { partnerData, setPartnerData } = useAssociatedPartnerData();

  const form = useForm<z.infer<typeof applicantSchema>>({
    resolver: zodResolver(applicantSchema),
    defaultValues: {
      email: ''
    }
  });

  const handleSubmit = async (values: z.infer<typeof applicantSchema>) => {
    setIsLoading(true);

    try {
      const amchamResponse = await verifyAssociated(values.email);

      if (amchamResponse.ok) {
        const data: CheckPartnershipResponse = await amchamResponse.json();

        await fetch('/api/amcham/create_partner', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            partner_checkout: {
              email: values.email
            }
          })
        });

        setPartnerData({ partnershipResponse: data, checkoutData: { email: values.email } });

        router.push({
          pathname: `/email_confirmation/${values.email}`,
        });
      }
      else {
        const errors: CheckPartnershipErrorResponse = await amchamResponse.json();
      }
    } catch (e) {
      console.error(e);
    }
    finally {
      setIsLoading(false);
    }
  }

  return (
    <section className="flex flex-col items-center mt-6 w-full">
      <div className="flex flex-col md:flex-row justify-center items-center">
        <h1 className="text-center text-2xl sm:text-4xl md:text-5xl md:pr-6 my-8">Conclua sua compra</h1>
        <Image
          src="/email.png"
          height={80}
          width={80}
          alt="Partner logo"
          layout="fixed"
          className="pointer-events-none"
        />
      </div>
      <div className="pt-6 w-3/4 sm:w-3/4 lg:w-3/5">
        <p className="text-center">Para continuar sua compra de assessoria visto adicione o seu e-mail corporativo, pois temos preços diferenciados para sócios AMCHAM</p>
      </div>
      <div className="flex flex-col items-center p-6 w-full sm:w-4/5">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="w-full flex flex-col items-center">
            <div className="py-4 space-y-3 w-full lg:w-1/2">
              <FormField
                control={form.control}
                name={'email'}
                render={({ field }) => (
                  <FormItem className="flex flex-col w-full">
                    <FormControl>
                      <Input {...field} type="email" placeholder="E-mail de afiliado" className="w-full my-4" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button type="submit" className="w-full lg:w-1/3 my-2 mt-4" disabled={isLoading}>Continuar</Button>
          </form>
        </Form>
        <Button className="w-full lg:w-3/5 my-2 mt-4" onClick={() => router.push('https://visto-sso.amcham.com.br/en?redirect=https://visto-dashboard.amcham.com.br/v3/travelers')}>Caso já tinha iniciado sua aplicação, clique aqui para continuar</Button>
      </div>
    </section>
  );
}

Home.getLayout = (page) => <DynamicLayout>{page}</DynamicLayout>;

export default Home;
